import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { TruckIcon as Truck, PlusIcon as Plus, TrashIcon as Trash, FloppyDiskIcon as FloppyDisk, BookOpenIcon as BookOpen, CaretDownIcon as CaretDown, CaretRightIcon as CaretRight, MagnifyingGlassIcon as MagnifyingGlass, CalendarBlankIcon as CalendarBlank, ClockCounterClockwiseIcon as ClockCounterClockwise } from "@phosphor-icons/react";
import { RicorrenzaCustomDialog } from "@/components/RicorrenzaCustomDialog";

const LOGBOOK_CAT_COLOR = {
  "Manutenzione": "bg-blue-100 text-blue-700 border-blue-200",
  "Incidente/Sinistro": "bg-red-100 text-red-700 border-red-200",
  "Lavaggio": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Rifornimento carburante": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Multa/Sanzione": "bg-orange-100 text-orange-700 border-orange-200",
};
const logbookCatColor = (c) => LOGBOOK_CAT_COLOR[c] || "bg-secondary text-foreground border-border";

// Mappa chiavi scadenze standard -> categoria logbook (storico attività)
const SCAD_TO_LOGBOOK_CAT = {
  tagliando: "Tagliando",
  revisione: "Revisione",
  cambio_gomme: "Cambio gomme",
  lavaggio: "Lavaggio",
  assicurazione: "Controllo/Verifica",
  bollo: "Controllo/Verifica",
  pronto_soccorso: "Controllo/Verifica",
  estintore: "Controllo/Verifica",
  albo_gestori_ambientali: "Controllo/Verifica",
};

function LogbookTimeline({ targa, active, refreshKey }) {
  const [voci, setVoci] = useState(null);
  useEffect(() => {
    if (!active) return;
    api.get(`/veicoli/${encodeURIComponent(targa)}/logbook`).then(({ data }) => setVoci(data.voci || [])).catch(() => setVoci([]));
  }, [targa, active, refreshKey]);
  if (voci === null) return <p className="text-sm text-muted-foreground py-6 text-center">Caricamento storico…</p>;
  if (voci.length === 0) return (
    <div className="rounded-sm border border-border bg-white px-4 py-8 text-center" data-testid={`storico-empty-${targa}`}>
      <ClockCounterClockwise size={30} className="text-muted-foreground mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">Nessuna attività registrata per questo mezzo.</p>
    </div>
  );
  return (
    <div className="space-y-2" data-testid={`storico-list-${targa}`}>
      {voci.map((v) => (
        <div key={v.id} className="rounded-sm border border-border bg-white p-3" data-testid={`storico-entry-${v.id}`}>
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Badge className={`rounded-sm text-[10px] ${logbookCatColor(v.categoria)}`}>{v.categoria}</Badge>
              <span className="text-[11px] text-muted-foreground">{new Date(v.created_at).toLocaleString("it-IT")}</span>
            </div>
            <span className="text-[11px] text-muted-foreground">{v.created_by}</span>
          </div>
          <div className="text-sm whitespace-pre-wrap">{v.nota}</div>
        </div>
      ))}
    </div>
  );
}

const daysUntil = (d) => {
  if (!d) return null;
  const t = new Date(d + "T00:00:00");
  if (isNaN(t)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((t - today) / 86400000);
};
const fmtIt = (d) => { if (!d) return "—"; const t = new Date(d + "T00:00:00"); return isNaN(t) ? "—" : t.toLocaleDateString("it-IT"); };
const _today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const _toStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const _clampMonth = (y, m, day) => { const last = new Date(y, m + 1, 0).getDate(); return new Date(y, m, Math.min(day, last)); };
// Avanza la scadenza di un periodo partendo dalla data attuale, garantendo sempre una data futura (mai retroattiva).
const addPeriod = (baseStr, ric) => {
  const tipo = ric?.tipo || "nessuna";
  if (tipo === "nessuna") return null;
  const today = _today();
  const parsed = baseStr ? new Date(baseStr + "T00:00:00") : null;
  const base = parsed && !isNaN(parsed) ? parsed : new Date(today);
  base.setHours(0, 0, 0, 0);
  const custom = ric?.custom;

  // Personalizzata · settimanale con giorni specifici → aggancia al prossimo giorno scelto futuro
  if (tipo === "personalizzata" && custom?.unita === "settimane" && Array.isArray(custom?.notifica_giorni_settimana) && custom.notifica_giorni_settimana.length) {
    const wanted = new Set(custom.notifica_giorni_settimana.map((g) => (Number(g) + 1) % 7)); // 0=Lun→JS 1 … 6=Dom→JS 0
    const start = base > today ? base : today;
    const cand = new Date(start); cand.setDate(cand.getDate() + 1);
    for (let i = 0; i < 3660; i++) { if (wanted.has(cand.getDay())) return _toStr(cand); cand.setDate(cand.getDate() + 1); }
    return null;
  }

  // Personalizzata · mensile con giorno del mese specifico → aggancia a quel giorno nel mese futuro
  if (tipo === "personalizzata" && custom?.unita === "mesi" && custom?.notifica_giorno_mese) {
    const n = Number(custom.intervallo) || 1;
    const day = Number(custom.notifica_giorno_mese);
    let k = 1;
    let c = _clampMonth(base.getFullYear(), base.getMonth() + n * k, day);
    while (c <= today && k < 1200) { k += 1; c = _clampMonth(base.getFullYear(), base.getMonth() + n * k, day); }
    return _toStr(c);
  }

  const step = (d) => {
    const nd = new Date(d);
    if (tipo === "giornaliera") nd.setDate(nd.getDate() + 1);
    else if (tipo === "settimanale") nd.setDate(nd.getDate() + 7);
    else if (tipo === "mensile") nd.setMonth(nd.getMonth() + 1);
    else if (tipo === "annuale") nd.setFullYear(nd.getFullYear() + 1);
    else if (tipo === "personalizzata") {
      const n = Number(custom?.intervallo) || 1; const u = custom?.unita || "giorni";
      if (u === "giorni") nd.setDate(nd.getDate() + n);
      else if (u === "settimane") nd.setDate(nd.getDate() + 7 * n);
      else if (u === "mesi") nd.setMonth(nd.getMonth() + n);
      else if (u === "anni") nd.setFullYear(nd.getFullYear() + n);
      else return null;
    } else return null;
    return nd;
  };

  let cand = step(base);
  if (!cand) return null;
  let guard = 0;
  while (cand <= today && guard < 4000) { const nx = step(cand); if (!nx) break; cand = nx; guard += 1; }
  return _toStr(cand);
};
const scadLabel = (n) => n === null ? "" : n < 0 ? `Scaduta ${Math.abs(n)}g` : n === 0 ? "Oggi" : n === 1 ? "Domani" : `${n} giorni`;
const scadTone = (n) => n === null ? "text-muted-foreground" : n <= 0 ? "text-red-600 font-semibold" : n <= 30 ? "text-amber-600" : "text-muted-foreground";

function ScadenzaBadge({ data }) {
  const n = daysUntil(data);
  if (!data) return <Badge variant="outline" className="rounded-sm text-muted-foreground text-[10px]">Non impostata</Badge>;
  if (n === null) return null;
  if (n < 0) return <Badge className="rounded-sm bg-red-100 text-red-700 border-red-200 text-[10px]" data-testid="badge-scaduta">Scaduta da {Math.abs(n)}g</Badge>;
  if (n <= 30) return <Badge className="rounded-sm bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="badge-in-scadenza">Tra {n}g</Badge>;
  return <Badge className="rounded-sm bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Tra {n}g</Badge>;
}

const GESTIONE_OPTS = [{ v: "azienda", l: "Azienda" }, { v: "noleggio", l: "Soc. noleggio" }];
const RIC_OPTS = [["nessuna", "Nessuna"], ["giornaliera", "Giornaliera"], ["settimanale", "Settimanale"], ["mensile", "Mensile"], ["annuale", "Annuale"], ["personalizzata", "Personalizzata"]];

function RicorrPicker({ value, onChange, onCustom, testid }) {
  const tipo = value?.tipo || "nessuna";
  return (
    <div className="flex items-center gap-1">
      <Select value={tipo} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm h-8 w-full text-[11px]" data-testid={testid}><SelectValue placeholder="Ricorrenza" /></SelectTrigger>
        <SelectContent>{RIC_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {tipo === "personalizzata" && <button type="button" className="text-[10px] text-primary underline whitespace-nowrap shrink-0" onClick={onCustom}>{value?.custom ? "✎" : "…"}</button>}
    </div>
  );
}

function Targa({ targa }) {
  return (
    <div className="inline-flex items-stretch rounded-[3px] overflow-hidden border border-zinc-300 shadow-sm shrink-0">
      <span className="bg-primary text-primary-foreground px-1 flex items-center justify-center text-[8px] font-bold leading-none">I</span>
      <span className="bg-white px-2 py-1 font-heading text-sm font-bold tracking-widest text-zinc-900">{targa}</span>
    </div>
  );
}

function VeicoloCard({ tipi, v, highlight, onSaved }) {
  const [form, setForm] = useState(v);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("scadenze");
  const [logRefresh, setLogRefresh] = useState(0);
  const navigate = useNavigate();
  useEffect(() => { setForm(v); }, [v]);
  useEffect(() => { if (highlight) setOpen(true); }, [highlight]);

  const dirty = JSON.stringify(form) !== JSON.stringify(v);
  const setStd = (key, field, val) => setForm((f) => ({ ...f, scadenze: { ...f.scadenze, [key]: { ...(f.scadenze[key] || {}), [field]: val } } }));
  const setCustom = (i, field, val) => setForm((f) => { const arr = [...(f.scadenze_custom || [])]; arr[i] = { ...arr[i], [field]: val }; return { ...f, scadenze_custom: arr }; });
  const addCustom = () => setForm((f) => ({ ...f, scadenze_custom: [...(f.scadenze_custom || []), { nome: "", data: "", gestione: "azienda" }] }));
  const removeCustom = (i) => setForm((f) => ({ ...f, scadenze_custom: (f.scadenze_custom || []).filter((_, j) => j !== i) }));

  const [customOpen, setCustomOpen] = useState(false);
  const [customTarget, setCustomTarget] = useState(null);
  const stdRicTipo = (key, val) => { const cur = form.scadenze[key]?.ricorrenza?.custom || null; setStd(key, "ricorrenza", { tipo: val, custom: val === "personalizzata" ? cur : null }); if (val === "personalizzata") { setCustomTarget({ t: "std", key }); setCustomOpen(true); } };
  const customRicTipo = (i, val) => { const cur = form.scadenze_custom[i]?.ricorrenza?.custom || null; setCustom(i, "ricorrenza", { tipo: val, custom: val === "personalizzata" ? cur : null }); if (val === "personalizzata") { setCustomTarget({ t: "custom", i }); setCustomOpen(true); } };
  const openCustom = (tgt) => { setCustomTarget(tgt); setCustomOpen(true); };
  const customVal = customTarget ? (customTarget.t === "std" ? form.scadenze[customTarget.key]?.ricorrenza?.custom : form.scadenze_custom[customTarget.i]?.ricorrenza?.custom) : null;
  const saveCustom = (cst) => { if (!customTarget) return; if (customTarget.t === "std") setStd(customTarget.key, "ricorrenza", { tipo: "personalizzata", custom: cst }); else setCustom(customTarget.i, "ricorrenza", { tipo: "personalizzata", custom: cst }); };

  const save = async () => { await doSave(); };
  const doSave = async (override = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put("/veicoli/scadenze", {
        targa: form.targa, nlt: form.nlt, societa_noleggio: form.societa_noleggio, in_manutenzione: form.in_manutenzione,
        note: form.note, scadenze: form.scadenze, scadenze_custom: (form.scadenze_custom || []).filter((c) => (c.nome || "").trim()),
        ...override,
      });
      toast.success(`Scadenze salvate per ${form.targa}`);
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const completaStd = async (key, label) => {
    const s = form.scadenze[key] || {};
    const next = addPeriod(s.data, s.ricorrenza);
    if (!next) { toast.error(`"${label}": imposta una ricorrenza per riprogrammare automaticamente la scadenza.`); return; }
    if (!window.confirm(`Segnare "${label}" come svolta oggi e riprogrammare la scadenza al ${fmtIt(next)}?`)) return;
    const newScad = { ...form.scadenze, [key]: { ...s, data: next } };
    setForm((f) => ({ ...f, scadenze: newScad }));
    await doSave({ scadenze: newScad });
    try {
      await api.post("/veicoli/logbook", {
        targa: form.targa,
        categoria: SCAD_TO_LOGBOOK_CAT[key] || "Altro",
        nota: `${label} svolta · prossima scadenza ${fmtIt(next)}`,
      });
      setLogRefresh((n) => n + 1);
    } catch (e) { /* lo storico non deve bloccare la riprogrammazione */ }
  };
  const completaCustom = async (i) => {
    const c = form.scadenze_custom[i] || {};
    const next = addPeriod(c.data, c.ricorrenza);
    if (!next) { toast.error(`"${c.nome || "Scadenza"}": imposta una ricorrenza per riprogrammare automaticamente.`); return; }
    if (!window.confirm(`Segnare "${c.nome}" come svolta oggi e riprogrammare la scadenza al ${fmtIt(next)}?`)) return;
    const arr = [...form.scadenze_custom]; arr[i] = { ...c, data: next };
    setForm((f) => ({ ...f, scadenze_custom: arr }));
    await doSave({ scadenze_custom: arr.filter((x) => (x.nome || "").trim()) });
    try {
      await api.post("/veicoli/logbook", {
        targa: form.targa,
        categoria: "Altro",
        nota: `${c.nome || "Scadenza"} svolta · prossima scadenza ${fmtIt(next)}`,
      });
      setLogRefresh((n) => n + 1);
    } catch (e) { /* lo storico non deve bloccare la riprogrammazione */ }
  };

  const nextScad = useMemo(() => {
    const all = [...Object.values(form.scadenze || {}), ...(form.scadenze_custom || [])];
    const ds = all.map((s) => daysUntil(s?.data)).filter((x) => x !== null);
    return ds.length ? Math.min(...ds) : null;
  }, [form]);

  return (
    <div id={`veicolo-${form.targa}`} className={`scroll-mt-24 border-b border-border last:border-0 ${highlight ? "bg-amber-50" : ""}`} data-testid={`veicolo-row-${form.targa}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left bg-sky-50 hover:bg-sky-100 transition-colors" data-testid={`veicolo-toggle-${form.targa}`}>
        <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0"><Truck size={16} className="text-muted-foreground" /></div>
        <Targa targa={form.targa} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{form.modello || form.marca || "Veicolo"}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {form.in_manutenzione && <Badge className="rounded-sm bg-orange-100 text-orange-700 border-orange-200 text-[10px]">In manutenzione</Badge>}
            {form.nlt && <Badge variant="outline" className="rounded-sm text-[10px]">NLT</Badge>}
          </div>
        </div>
        {nextScad !== null && <span className={`text-sm mr-1 ${scadTone(nextScad)}`}>{scadLabel(nextScad)}</span>}
        {dirty && <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse mr-1" title="Modifiche non salvate" />}
        {open ? <CaretDown size={16} className="text-muted-foreground shrink-0" /> : <CaretRight size={16} className="text-muted-foreground shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 bg-secondary/20 border-l-2 border-primary" data-testid={`veicolo-detail-${form.targa}`}>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={!!form.in_manutenzione} onCheckedChange={(val) => setForm((f) => ({ ...f, in_manutenzione: val }))} data-testid={`veicolo-manutenzione-sw-${form.targa}`} /> In manutenzione</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={!!form.nlt} onCheckedChange={(val) => setForm((f) => ({ ...f, nlt: val }))} data-testid={`veicolo-nlt-${form.targa}`} /> Noleggio (NLT)</label>
            {form.nlt && <Input value={form.societa_noleggio || ""} onChange={(e) => setForm((f) => ({ ...f, societa_noleggio: e.target.value }))} placeholder="Società di noleggio" className="rounded-sm h-9 w-56" data-testid={`veicolo-noleggio-${form.targa}`} />}
            <button type="button" onClick={() => navigate(`/scadenziario/automezzi/${encodeURIComponent(form.targa)}/logbook`)} className="text-xs text-primary hover:underline flex items-center gap-1 ml-auto" data-testid={`logbook-btn-${form.targa}`}><BookOpen size={13} /> Logbook</button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-1">
            <TabsList className="rounded-sm">
              <TabsTrigger value="scadenze" className="rounded-sm text-xs" data-testid={`tab-scadenze-${form.targa}`}>Scadenze</TabsTrigger>
              <TabsTrigger value="storico" className="rounded-sm text-xs" data-testid={`tab-storico-${form.targa}`}>Storico attività</TabsTrigger>
            </TabsList>
            <TabsContent value="scadenze" className="mt-3">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Scadenze del veicolo</Label>
          <div className="mt-2 rounded-sm border border-border bg-white divide-y divide-border">
            {tipi.map((t) => {
              const s = form.scadenze[t.key] || { data: "", gestione: "azienda" };
              return (
                <div key={t.key} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`scad-row-${t.key}-${form.targa}`}>
                  <span className="text-sm font-medium w-40 shrink-0">{t.label}</span>
                  <Input type="date" value={s.data || ""} onChange={(e) => setStd(t.key, "data", e.target.value)} className="rounded-sm h-8 text-xs w-40" data-testid={`scad-${t.key}-${form.targa}`} />
                  <Select value={s.gestione || "azienda"} onValueChange={(val) => setStd(t.key, "gestione", val)}>
                    <SelectTrigger className="rounded-sm h-8 w-[120px] text-[11px]" data-testid={`gest-${t.key}-${form.targa}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{GESTIONE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="w-36"><RicorrPicker value={s.ricorrenza} onChange={(val) => stdRicTipo(t.key, val)} onCustom={() => openCustom({ t: "std", key: t.key })} testid={`ric-${t.key}-${form.targa}`} /></div>
                  <div className="ml-auto flex items-center gap-2.5">
                    <ScadenzaBadge data={s.data} />
                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer whitespace-nowrap" title="Segna attività svolta oggi e riprogramma la scadenza secondo la ricorrenza">
                      <Checkbox checked={false} onCheckedChange={() => completaStd(t.key, t.label)} data-testid={`svolta-${t.key}-${form.targa}`} /> Svolta
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-4 mb-2">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Scadenze personalizzate</Label>
            <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={addCustom} data-testid={`add-custom-${form.targa}`}><Plus size={13} /> Aggiungi</Button>
          </div>
          <div className="space-y-2">
            {(form.scadenze_custom || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna scadenza personalizzata.</p>}
            {(form.scadenze_custom || []).map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 border border-border rounded-sm px-3 py-2 bg-white" data-testid={`custom-row-${form.targa}-${i}`}>
                <Input value={c.nome || ""} onChange={(e) => setCustom(i, "nome", e.target.value)} placeholder="Nome scadenza" className="rounded-sm h-9 w-40 shrink-0" />
                <Select value={c.gestione || "azienda"} onValueChange={(val) => setCustom(i, "gestione", val)}>
                  <SelectTrigger className="rounded-sm h-9 w-36 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{GESTIONE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="date" value={c.data || ""} onChange={(e) => setCustom(i, "data", e.target.value)} className="rounded-sm h-9 w-44 shrink-0" />
                <div className="w-40 shrink-0"><RicorrPicker value={c.ricorrenza} onChange={(val) => customRicTipo(i, val)} onCustom={() => openCustom({ t: "custom", i })} testid={`cust-ric-${form.targa}-${i}`} /></div>
                <div className="shrink-0"><ScadenzaBadge data={c.data} /></div>
                <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer whitespace-nowrap shrink-0" title="Segna attività svolta oggi e riprogramma la scadenza secondo la ricorrenza">
                  <Checkbox checked={false} onCheckedChange={() => completaCustom(i)} data-testid={`svolta-custom-${form.targa}-${i}`} /> Svolta
                </label>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeCustom(i)}><Trash size={14} /></Button>
              </div>
            ))}
          </div>

          <div className="mt-4"><Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Note</Label><Textarea value={form.note || ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="mt-1 rounded-sm text-sm bg-white" /></div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-muted-foreground">{form.updated_at ? `Ultima modifica: ${new Date(form.updated_at).toLocaleString("it-IT")}${form.updated_by ? " · da " + form.updated_by : ""}` : ""}</span>
            <Button onClick={save} disabled={saving || !dirty} className="rounded-sm gap-2" data-testid={`save-veicolo-${form.targa}`}><FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio…" : "Salva"}</Button>
          </div>
            </TabsContent>
            <TabsContent value="storico" className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Storico attività del mezzo</Label>
                <button type="button" onClick={() => navigate(`/scadenziario/automezzi/${encodeURIComponent(form.targa)}/logbook`)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`storico-add-${form.targa}`}><Plus size={12} /> Aggiungi voce</button>
              </div>
              <LogbookTimeline targa={form.targa} active={open && tab === "storico"} refreshKey={logRefresh} />
            </TabsContent>
          </Tabs>
          <RicorrenzaCustomDialog open={customOpen} onOpenChange={setCustomOpen} value={customVal} onSave={saveCustom} />
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, tone, testid }) {
  return (
    <div className="flex-1 min-w-[140px] rounded-sm border border-border bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-bold tabular-nums ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function ScadenziarioAutomezzi() {
  const [tipi, setTipi] = useState([]);
  const [veicoli, setVeicoli] = useState(null);
  const [params] = useSearchParams();
  const targaParam = params.get("targa");
  const [highlight, setHighlight] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/veicoli/scadenze").then(({ data }) => { setTipi(data.tipi_standard || []); setVeicoli(data.veicoli || []); }).catch(() => setVeicoli([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!targaParam || veicoli === null) return;
    const el = document.getElementById(`veicolo-${targaParam}`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setHighlight(targaParam); const t = setTimeout(() => setHighlight(null), 2800); return () => clearTimeout(t); }
  }, [targaParam, veicoli]);

  const goToVeicolo = (targa) => {
    setHighlight(targa);
    requestAnimationFrame(() => {
      const el = document.getElementById(`veicolo-${targa}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    setTimeout(() => setHighlight(null), 2800);
  };

  const tipoLabel = useMemo(() => Object.fromEntries((tipi || []).map((t) => [t.key, t.label])), [tipi]);

  const stats = useMemo(() => {
    let oggi = 0, entro30 = 0;
    (veicoli || []).forEach((v) => {
      [...Object.values(v.scadenze || {}), ...(v.scadenze_custom || [])].forEach((s) => {
        const d = daysUntil(s?.data); if (d === null) return;
        if (d <= 0) oggi++; if (d >= 0 && d <= 30) entro30++;
      });
    });
    return { veicoli: (veicoli || []).length, oggi, entro30 };
  }, [veicoli]);

  const prossime = useMemo(() => {
    const out = [];
    (veicoli || []).forEach((v) => {
      Object.entries(v.scadenze || {}).forEach(([k, s]) => { const d = daysUntil(s?.data); if (d !== null && d <= 30) out.push({ targa: v.targa, modello: v.modello || v.marca, attivita: tipoLabel[k] || k, data: s.data, d }); });
      (v.scadenze_custom || []).forEach((s) => { const d = daysUntil(s?.data); if (d !== null && d <= 30) out.push({ targa: v.targa, modello: v.modello || v.marca, attivita: s.nome, data: s.data, d }); });
    });
    return out.sort((a, b) => a.d - b.d);
  }, [veicoli, tipoLabel]);

  const filtered = (veicoli || []).filter((v) => { const hay = `${v.targa} ${v.modello || ""} ${v.marca || ""}`.toLowerCase(); return !q || hay.includes(q.toLowerCase()); }).sort((a, b) => (a.targa || "").localeCompare(b.targa || "", "it", { numeric: true, sensitivity: "base" }));

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10 max-w-5xl">
        <PageHeader title="Automezzi" subtitle="Gestione flotta e scadenze dei veicoli aziendali" />
        {veicoli === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : veicoli.length === 0 ? (
          <Card className="rounded-sm border-border shadow-none p-8 text-center" data-testid="no-veicoli">
            <Truck size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nessun veicolo configurato. Aggiungi le targhe in <b>Impostazioni → Veicoli aziendali</b>.</p>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 mb-6" data-testid="automezzi-stats">
              <StatTile label="Veicoli" value={stats.veicoli} testid="stat-veicoli" />
              <StatTile label="Scadenze oggi" value={stats.oggi} tone={stats.oggi > 0 ? "text-red-600" : ""} testid="stat-oggi" />
              <StatTile label="Entro 30 giorni" value={stats.entro30} tone={stats.entro30 > 0 ? "text-amber-600" : ""} testid="stat-30" />
            </div>

            <Card className="rounded-sm border-border shadow-none mb-6" data-testid="prossime-card">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <CalendarBlank size={18} className="text-primary" />
                <div><h3 className="font-heading font-semibold text-sm">Scadenze prossimi 30 giorni</h3><p className="text-xs text-muted-foreground">Attività che richiedono attenzione</p></div>
                <Badge variant="outline" className="rounded-sm ml-auto text-xs">{prossime.length}</Badge>
              </div>
              {prossime.length === 0 ? (
                <p className="text-sm text-muted-foreground px-4 py-6 text-center" data-testid="prossime-empty">Nessuna scadenza nei prossimi 30 giorni.</p>
              ) : (
                <div className="divide-y divide-border max-h-80 overflow-y-auto">
                  {prossime.map((p, i) => (
                    <button key={i} type="button" onClick={() => goToVeicolo(p.targa)} className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/40" data-testid={`prossima-${i}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.attivita}</div>
                        <div className="text-xs text-muted-foreground">{p.targa} · {fmtIt(p.data)}</div>
                      </div>
                      <span className={`text-sm shrink-0 ${scadTone(p.d)}`}>{scadLabel(p.d)}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card className="rounded-sm border-border shadow-none overflow-hidden" data-testid="veicoli-card">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <h3 className="font-heading font-semibold text-sm">Veicoli aziendali</h3>
                <Badge variant="outline" className="rounded-sm text-xs">{filtered.length} di {veicoli.length}</Badge>
                <div className="relative ml-auto w-64">
                  <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per targa o modello…" className="rounded-sm h-9 pl-8" data-testid="automezzi-f-targa" />
                </div>
              </div>
              <div data-testid="veicoli-list">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-4 py-8 text-center" data-testid="no-veicoli-filtered">Nessun veicolo corrisponde alla ricerca.</p>
                ) : filtered.map((v) => (
                  <VeicoloCard key={v.targa} tipi={tipi} v={v} highlight={highlight === v.targa} onSaved={(d) => setVeicoli((list) => list.map((x) => x.targa === d.targa ? { ...x, ...d } : x))} />
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </InternalLayout>
  );
}
